import axios from 'axios';
import * as tus from 'tus-js-client';
import {
  TrackedJob,
  TrackedJobEvent,
  PagesResponse,
  LinkedMetadata,
  DocumentSummary,
  DatasetListItem,
  DatasetSummary,
  DocumentOverviewQueryResult,
  DocumentOverviewItem,
  TaxonomyTreeNode,
  DocumentSummaryStats,
  DocumentTag,
  DocumentWarning,
  DocumentReviewStatus,
  DocumentDetailData,
  DocumentDetailHeaderData,
  DocumentOcrExcerpt,
  DocumentBoundaryInfo,
  DocumentTargetOption,
  ActiveJob,
  DocumentDate,
  DocumentTaxonomy,
  ReorderStatus,
  UserPreferences,
} from '../types';
import type { DuplicatePagesResponse } from '../components/DuplicatesModal';

export type UploadTelemetryEventType =
  | 'session_start'
  | 'chunk_start'
  | 'chunk_success'
  | 'chunk_failure'
  | 'retry'
  | 'session_heartbeat'
  | 'session_complete'
  | 'abort'
  | 'resume';

export interface UploadTelemetryEvent {
  uploadId: string;
  fileFingerprint: string;
  eventType: UploadTelemetryEventType;
  byteOffset?: number;
  httpStatus?: number | null;
  errorClass?: string | null;
  userAgent?: string;
  connectionType?: string;
  durationMs?: number;
  attemptNum?: number;
  fileSize?: number;
  fileName?: string;
}

// Filevine picker calls fail fast so the modal's retry affordance is
// reachable, instead of hanging on the axios default (no timeout) until
// nginx's 300s proxy_read_timeout cuts the connection.
const FILEVINE_PICKER_TIMEOUT_MS = 25_000;

// In production, the backend serves the UI at /ui — same origin, so
// relative paths work. In development, the CRA proxy forwards requests
// to the backend. REACT_APP_API_URL can override for non-standard setups.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  withCredentials: true, // Required for cookie-based auth in production
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each item as a separate parameter
          value.forEach(item => {
            if (item !== undefined && item !== null) {
              searchParams.append(key, String(item));
            }
          });
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    }
  }
});

// When the session cookie expires, redirect to login once (Azure SSO will
// silently re-issue a token) and restore the user's current location.
let sessionExpiredRedirecting = false;
api.interceptors.response.use(undefined, (error) => {
  if (
    error?.response?.status === 401
    && !sessionExpiredRedirecting
    && !window.location.pathname.startsWith('/auth/')
  ) {
    sessionExpiredRedirecting = true;
    const returnPath = encodeURIComponent(window.location.hash || '#/datasets');
    window.location.href = `/auth/login?return_path=${returnPath}`;
  }
  return Promise.reject(error);
});

type RawDocumentOverviewItem = {
  doc_id: string;
  title?: string | null;
  page_count: number;
  confidence?: number | null;
  status: string;
  tags?: string[];
  warnings?: string[];
  last_edited_at?: string | null;
  last_edited_by?: string | null;
  is_manual?: boolean;
  thumbnail_refs?: number[];
  ocr_hits?: number | null;
  search_snippet?: string | null;
  low_confidence_pages?: number;
  trailing_boundary_uncertain?: boolean;
  duplicate_pair_count?: number;
  internal_duplicate_count?: number;
  external_duplicate_count?: number;
  is_duplicate_of_other_doc?: boolean;
  duplicate_of_ratio?: number | null;
  first_page_index?: number | null;
  date?: RawDocumentDate;
  taxonomy?: RawTaxonomy;
  upload_date?: string | null;
  source_filename?: string | null;
  source_page_range?: string | null;
};

type RawTaxonomy = {
  section: string;
  leaf?: string | null;
  sub_leaf?: string | null;
  certainty?: string | null;
  section_logprob?: number | null;
  classification_failed?: boolean | null;
  confirmed?: boolean | null;
  confirmed_by?: string | null;
  confirmed_at?: string | null;
  display_label: string;
  canonical_rank: number;
} | null | undefined;

const mapTaxonomy = (raw: RawTaxonomy): DocumentTaxonomy | null =>
  raw
    ? {
        section: raw.section,
        leaf: raw.leaf ?? null,
        subLeaf: raw.sub_leaf ?? null,
        certainty: (raw.certainty as 'high' | 'medium' | 'low' | null) ?? null,
        sectionLogprob: raw.section_logprob ?? null,
        classificationFailed: Boolean(raw.classification_failed),
        confirmed: Boolean(raw.confirmed),
        confirmedBy: raw.confirmed_by ?? null,
        confirmedAt: raw.confirmed_at ?? null,
        displayLabel: raw.display_label,
        canonicalRank: raw.canonical_rank,
      }
    : null;

// The API sends the date object in snake_case (no_date); the FE DocumentDate
// uses noDate. Map it (and tolerate the object being absent/null).
type RawDocumentDate = {
  raw?: string | null;
  normalized?: string;
  year?: number;
  month?: number;
  day?: number;
  precision?: 'day' | 'month' | 'year' | null;
  no_date?: boolean;
  confirmed?: boolean;
  confirmed_by?: string | null;
  confirmed_at?: string | null;
};

const mapDate = (date: RawDocumentDate | null | undefined): DocumentDate | undefined => {
  if (!date) return undefined;
  return {
    raw: date.raw ?? null,
    normalized: date.normalized,
    year: date.year,
    month: date.month,
    day: date.day,
    precision: date.precision ?? null,
    noDate: Boolean(date.no_date),
    confirmed: Boolean(date.confirmed),
    confirmedBy: date.confirmed_by ?? null,
    confirmedAt: date.confirmed_at ?? null,
  };
};

const toDocumentTag = (tag: string): DocumentTag => ({
  id: tag,
  label: tag,
  tone: 'default',
});

const toDocumentWarning = (warning: string): DocumentWarning => ({
  id: warning,
  label: warning,
  tone: 'warning',
});

const buildThumbnailUrls = (
  refs: number[] | undefined,
  encodedMetadata: string,
): (string | null)[] => {
  if (!refs || refs.length === 0) {
    return [null, null, null];
  }
  const urls: (string | null)[] = refs.map(ref => `/pages/full/${ref}?metadata=${encodedMetadata}`);
  while (urls.length < 3) {
    urls.push(null);
  }
  return urls.slice(0, 3);
};

const mapDocumentOverviewItem = (
  doc: RawDocumentOverviewItem,
  encodedMetadata: string,
): DocumentOverviewItem => {
  const thumbnailUrls = buildThumbnailUrls(doc.thumbnail_refs, encodedMetadata);

  return {
    docId: doc.doc_id,
    title: doc.title ?? doc.doc_id,
    pageCount: doc.page_count ?? 0,
    confidence: doc.confidence ?? null,
    status: (doc.status as DocumentReviewStatus) ?? 'unreviewed',
    tags: (doc.tags ?? []).map(toDocumentTag),
    taxonomy: mapTaxonomy(doc.taxonomy),
    warnings: (doc.warnings ?? []).map(toDocumentWarning),
    lastEditedAt: doc.last_edited_at ?? undefined,
    lastEditedBy: doc.last_edited_by ?? undefined,
    isManual: Boolean(doc.is_manual),
    thumbnailUrls,
    thumbnailRefs: doc.thumbnail_refs ?? [],
    ocrHitCount: doc.ocr_hits ?? undefined,
    searchSnippet: doc.search_snippet ?? undefined,
    lowConfidencePages: doc.low_confidence_pages ?? 0,
    trailingBoundaryUncertain: doc.trailing_boundary_uncertain ?? false,
    duplicatePairCount: doc.duplicate_pair_count ?? 0,
    internalDuplicateCount: doc.internal_duplicate_count ?? 0,
    externalDuplicateCount: doc.external_duplicate_count ?? 0,
    isDuplicateOfOtherDoc: doc.is_duplicate_of_other_doc ?? false,
    duplicateOfRatio: doc.duplicate_of_ratio ?? null,
    firstPageIndex: doc.first_page_index ?? undefined,
    date: mapDate(doc.date),
    uploadDate: doc.upload_date ?? undefined,
    sourceFilename: doc.source_filename ?? undefined,
    sourcePageRange: doc.source_page_range ?? undefined,
  };
};

const mapActiveJob = (job: any): ActiveJob => ({
  jobId: String(job.job_id ?? job.jobId ?? ''),
  status: job.status ?? 'PENDING',
  progress: typeof job.progress === 'number' && !Number.isNaN(job.progress) ? job.progress : 0,
  message: job.message ?? null,
  phase: job.phase ?? null,
  phaseProgress: typeof job.phase_progress === 'number' ? job.phase_progress : null,
  phaseCompleted: typeof job.phase_completed === 'number' ? job.phase_completed : null,
  phaseTotal: typeof job.phase_total === 'number' ? job.phase_total : null,
  totalGaps: typeof job.total_gaps === 'number' ? job.total_gaps : 0,
  finishedGaps: typeof job.finished_gaps === 'number' ? job.finished_gaps : 0,
  error: job.error ?? null,
  result: job.result ?? null,
  cancelled: job.cancelled ?? null,
  jobType: job.job_type ?? job.jobType ?? null,
  datasetPath: job.dataset_path ?? job.datasetPath ?? null,
  metadataPath: job.metadata_path ?? job.metadataPath ?? null,
  pdfPath: job.pdf_path ?? job.pdfPath ?? null,
  createdAt: typeof job.created_at === 'number' ? job.created_at : null,
  updatedAt: typeof job.updated_at === 'number' ? job.updated_at : (typeof job.created_at === 'number' ? job.created_at : null),
  caseId: job.case_id ?? job.caseId ?? (job.payload?.case_id ?? null) ?? null,
  sourceFilename: deriveJobSourceFilename(job),
});

/** The source filename a job is processing, from its payload, for display in
 *  the jobs queue. Filevine pulls carry `filevine_filename`; uploads/ingest
 *  carry a `files[].filename` manifest (joined when there are several). */
const deriveJobSourceFilename = (job: any): string | null => {
  const p = job.payload;
  if (!p || typeof p !== 'object') return null;
  if (typeof p.filevine_filename === 'string' && p.filevine_filename) {
    return p.filevine_filename;
  }
  const files = p.files;
  if (Array.isArray(files) && files.length) {
    const names = files
      .map((f: any) => (f && typeof f.filename === 'string' ? f.filename : null))
      .filter(Boolean) as string[];
    if (names.length === 1) return names[0];
    if (names.length > 1) return `${names[0]} (+${names.length - 1} more)`;
  }
  return null;
};

export interface UpdateDocumentPayload {
  // NOTE: no `status` — review status is DERIVED from confirming title/date/
  // category and cannot be set directly.
  title?: string;
  /** Approve the current title as-is (review without renaming). */
  confirmTitle?: boolean;
  tags?: string[];
  confidenceOverride?: number | null;
  notes?: string | null;
  lastEditedBy?: string;
  date?: DocumentDate | null;
  /** Approve the current date as-is (review without changing value). */
  confirmDate?: boolean;
  /** Manual category override (an edit is implicitly correct). */
  taxonomy?: { section: string; leaf?: string | null; subLeaf?: string | null };
  /** Approve the current auto category as-is (review without changing value). */
  confirmTaxonomy?: boolean;
}

export const apiService = {
  async fetchCurrentUser(options?: { signal?: AbortSignal }): Promise<{ name: string; email: string; id: string }> {
    const response = await api.get<{ name: string; email: string; id: string }>('/auth/user', {
      signal: options?.signal,
    });
    return response.data;
  },

  async fetchUserPreferences(options?: { signal?: AbortSignal }): Promise<UserPreferences> {
    const response = await api.get<UserPreferences>('/user/preferences', {
      signal: options?.signal,
    });
    return response.data;
  },

  async patchUserPreferences(
    patch: { markSeen?: string | string[]; suppressAll?: boolean; resetTutorial?: boolean },
  ): Promise<UserPreferences> {
    const body: Record<string, unknown> = {};
    if (patch.markSeen !== undefined) body.mark_seen = patch.markSeen;
    if (patch.suppressAll !== undefined) body.suppress_all = patch.suppressAll;
    if (patch.resetTutorial !== undefined) body.reset_tutorial = patch.resetTutorial;
    const response = await api.patch<UserPreferences>('/user/preferences', body);
    return response.data;
  },

  async fetchDatasets(): Promise<DatasetListItem[]> {
    const response = await api.get<{
      datasets: Array<{
        dataset_id: string;
        metadata_path: string;
        title: string;
        last_modified: number;
        document_count?: number;
        page_count?: number;
        review_completion?: number;
        last_saved_by?: string | null;
        warnings?: string[];
      }>;
    }>('/datasets');

    const coerceNumber = (value: unknown, fallback = 0): number => {
      const numeric = typeof value === 'string' ? Number(value) : value;
      return typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : fallback;
    };

    return response.data.datasets.map(dataset => ({
      datasetId: dataset.dataset_id,
      metadataPath: dataset.metadata_path,
      title: dataset.title,
      lastModified: coerceNumber(dataset.last_modified, 0),
      documentCount: coerceNumber(dataset.document_count, 0),
      pageCount: coerceNumber(dataset.page_count, 0),
      reviewCompletion: coerceNumber(dataset.review_completion, 0),
      lastSavedBy: dataset.last_saved_by ?? undefined,
      warnings: dataset.warnings ?? [],
    }));
  },

  async deleteDataset(metadataPath: string): Promise<void> {
    const encoded = encodeURIComponent(metadataPath);
    await api.delete(`/datasets/${encoded}`);
  },

  async fetchDatasetSummary(metadataPath: string): Promise<DatasetSummary> {
    const response = await api.get<{
      dataset_id: string;
      title: string;
      summary: {
        total_documents: number;
        total_pages: number;
        orphan_pages: number;
        duplicate_pairs: number;
        low_confidence_documents: number;
        review_completion: number;
      };
      review_status_label?: string;
      last_updated_at?: string;
      last_updated_by?: string;
      warnings?: string[];
      filevine_project_id?: number | null;
      filevine_project_name?: string | null;
    }>(`/datasets/${encodeURIComponent(metadataPath)}/summary`);

    const summaryData = response.data.summary;
    const mappedSummary: DocumentSummaryStats = {
      totalDocuments: summaryData.total_documents ?? 0,
      totalPages: summaryData.total_pages ?? 0,
      duplicatePairs: summaryData.duplicate_pairs ?? 0,
      lowConfidenceDocuments: summaryData.low_confidence_documents ?? 0,
      reviewCompletion: summaryData.review_completion ?? 0,
      orphanPages: summaryData.orphan_pages ?? 0,
    };

    return {
      datasetId: response.data.dataset_id,
      title: response.data.title,
      summary: mappedSummary,
      reviewStatusLabel: response.data.review_status_label,
      lastUpdatedAt: response.data.last_updated_at,
      lastUpdatedBy: response.data.last_updated_by,
      warnings: response.data.warnings ?? [],
      filevineProjectId: response.data.filevine_project_id ?? null,
      filevineProjectName: response.data.filevine_project_name ?? null,
    };
  },

  async fetchDatasetTags(metadataPath: string): Promise<string[]> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const response = await api.get<{ tags: string[] }>(`/datasets/${encodedMetadata}/tags`);
    return response.data.tags ?? [];
  },

  async createDatasetTag(metadataPath: string, name: string): Promise<string[]> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const response = await api.post<{ tags: string[] }>(`/datasets/${encodedMetadata}/tags`, { name });
    return response.data.tags ?? [];
  },

  async fetchDatasetDocuments(
    metadataPath: string,
    options: {
      search?: string;
      status?: string;
      category?: string;
      dateRange?: { start?: string; end?: string } | null;
      sort?: string;
      limit?: number;
      offset?: number;
      tags?: string[];
      sharesDuplicatesWith?: string;
    } = {},
  ): Promise<DocumentOverviewQueryResult> {
    const params: Record<string, unknown> = {
      sort: options.sort ?? 'nominal',
      limit: options.limit ?? 10,
      offset: options.offset ?? 0,
    };

    if (options.search) {
      params.search = options.search;
    }
    if (options.status) {
      params.status = options.status;
    }
    if (options.category) {
      params.category = options.category;
    }
    if (options.dateRange?.start) {
      params.date_start = options.dateRange.start;
    }
    if (options.dateRange?.end) {
      params.date_end = options.dateRange.end;
    }
    if (options.tags && options.tags.length > 0) {
      params.tags = options.tags.join(',');
    }
    if (options.sharesDuplicatesWith) {
      params.shares_duplicates_with = options.sharesDuplicatesWith;
    }

    const encodedMetadata = encodeURIComponent(metadataPath);

    const response = await api.get<{
      documents: RawDocumentOverviewItem[];
      total: number;
      filter_counts: Record<string, number>;
      warnings?: string[];
    }>(`/datasets/${encodedMetadata}/documents`, {
      params,
    });

    const documents: DocumentOverviewItem[] = response.data.documents.map(doc =>
      mapDocumentOverviewItem(doc, encodedMetadata),
    );

    return {
      documents,
      total: response.data.total,
      filterCounts: response.data.filter_counts ?? {},
      warnings: response.data.warnings ?? [],
    };
  },

  /** The checklist taxonomy tree (sections -> leaves) for the category filter.
   * Node `value`s are the same strings the documents endpoint's `category` param
   * expects (bare section, or `section/leaf`). */
  async fetchTaxonomySections(): Promise<TaxonomyTreeNode[]> {
    const response = await api.get<{ tags: TaxonomyTreeNode[] }>('/taxonomy/sections');
    return response.data.tags ?? [];
  },

  async fetchDocumentDetail(
    metadataPath: string,
    docId: string,
    options: { search?: string } = {},
  ): Promise<DocumentDetailData> {
    const params: Record<string, string> = {};
    if (options.search) {
      params.search = options.search;
    }

    const response = await api.get<{
      header: {
        case_name: string;
        document_title: string;
        document_id: string;
        status: string;
        status_options: string[];
        confidence?: number | null;
        confidence_label?: string;
        tags?: string[];
        taxonomy?: RawTaxonomy;
        date?: RawDocumentDate;
        title_confirmation?: { confirmed?: boolean; confirmed_by?: string | null; confirmed_at?: string | null } | null;
        auto_save_message?: string;
        warnings?: string[];
        actions?: string[];
        active_editors?: string[];
        notes?: string | null;
      };
      page_rail: Array<{
        page_id: string;
        page_number: number;
        label?: string | null;
        thumbnail_url?: string | null;
        warnings?: string[];
        notes_count?: number;
        boundary_confidence?: number | null;
        page_index?: number | null;
        notes?: string[];
        ocr_preview?: string | null;
        reorder_status?: string | null;
        reorder_confidence?: number | null;
        printed_page_number?: number | null;
        internal_duplicate?: boolean;
        external_duplicate?: boolean;
        source_pdf?: string | null;
        source_page_index?: number | null;
      }>;
      canvas: Array<{
        page_id: string;
        page_number: number;
        label?: string | null;
        image_url?: string | null;
        warnings?: string[];
        notes?: string[];
        page_index?: number | null;
        width?: number | null;
        height?: number | null;
      }>;
      ocr_excerpts?: Array<{
        id: string;
        page_number: number;
        snippet: string;
        confidence?: number | null;
        title?: string | null;
        tags?: string[];
      }>;
      warnings?: string[];
      duplicate_pairs?: Array<{
        left: {
          doc_id: string;
          doc_title?: string | null;
          page_id: string;
          page_number?: number | null;
          page_index?: number | null;
          page_name?: string | null;
        };
        right: {
          doc_id: string;
          doc_title?: string | null;
          page_id: string;
          page_number?: number | null;
          page_index?: number | null;
          page_name?: string | null;
        };
        cosine_similarity: number;
      }>;
      previous_document?: {
        doc_id: string;
        title?: string | null;
        pages: Array<{
          page_id: string;
          page_number: number;
          thumbnail_url?: string | null;
          ocr_preview?: string | null;
          page_index?: number | null;
        }>;
        total_pages?: number;
      } | null;
      next_document?: {
        doc_id: string;
        title?: string | null;
        pages: Array<{
          page_id: string;
          page_number: number;
          thumbnail_url?: string | null;
          ocr_preview?: string | null;
          page_index?: number | null;
        }>;
        total_pages?: number;
      } | null;
    }>(`/datasets/${encodeURIComponent(metadataPath)}/documents/${encodeURIComponent(docId)}`, {
      params,
    });

    const mapWarnings = (raw: string[] | undefined): DocumentWarning[] =>
      (raw ?? []).map(warning => ({ id: warning, label: warning, tone: 'warning' }));

    const headerRaw = response.data.header;
    const header: DocumentDetailHeaderData = {
      caseName: headerRaw.case_name,
      documentTitle: headerRaw.document_title,
      documentId: headerRaw.document_id,
      status: (headerRaw.status as DocumentReviewStatus) ?? 'unreviewed',
      statusOptions: (headerRaw.status_options ?? []) as DocumentReviewStatus[],
      tags: headerRaw.tags ?? [],
      taxonomy: mapTaxonomy(headerRaw.taxonomy),
      date: mapDate(headerRaw.date),
      titleConfirmation: headerRaw.title_confirmation
        ? {
            confirmed: Boolean(headerRaw.title_confirmation.confirmed),
            confirmedBy: headerRaw.title_confirmation.confirmed_by ?? null,
            confirmedAt: headerRaw.title_confirmation.confirmed_at ?? null,
          }
        : null,
      autoSaveMessage: headerRaw.auto_save_message,
      warnings: headerRaw.warnings ?? [],
      actions: headerRaw.actions ?? [],
      activeEditors: headerRaw.active_editors ?? [],
      notes: headerRaw.notes ?? undefined,
    };

    const pageRail = response.data.page_rail.map(item => ({
      id: item.page_id,
      pageNumber: item.page_number,
      label: item.label ?? undefined,
      thumbnailUrl: item.thumbnail_url ?? undefined,
      warnings: mapWarnings(item.warnings),
      notesCount: item.notes_count ?? 0,
      notes: Array.isArray(item.notes) ? item.notes : undefined,
      ocrPreview: item.ocr_preview ?? undefined,
      boundaryAfter:
        item.boundary_confidence != null
          ? ({
              id: `${docId}:${Math.max(0, item.page_number - 1)}-boundary`,
              confidence: item.boundary_confidence,
              warning:
                item.boundary_confidence >= 0.4 && item.boundary_confidence <= 0.6
                  ? 'Low confidence'
                  : undefined,
              suggested: false,
            } as DocumentBoundaryInfo)
          : null,
      pageIndex: item.page_index ?? null,
      reorderStatus: (item.reorder_status as ReorderStatus) ?? null,
      reorderConfidence: item.reorder_confidence ?? null,
      printedPageNumber: item.printed_page_number ?? null,
      internalDuplicate: item.internal_duplicate ?? false,
      externalDuplicate: item.external_duplicate ?? false,
      sourcePdf: item.source_pdf ?? null,
      sourcePageIndex: item.source_page_index ?? null,
    }));

    const railByPageId = new Map(pageRail.map(p => [p.id, p]));
    const canvas = response.data.canvas.map(item => {
      const rail = railByPageId.get(item.page_id);
      return {
        pageId: item.page_id,
        pageNumber: item.page_number,
        label: item.label ?? undefined,
        imageUrl: item.image_url ?? undefined,
        warnings: mapWarnings(item.warnings),
        notes: item.notes ?? [],
        pageIndex: item.page_index ?? null,
        width: item.width ?? null,
        height: item.height ?? null,
        sourcePdf: rail?.sourcePdf ?? null,
        sourcePageIndex: rail?.sourcePageIndex ?? null,
        boundaryAfter: rail?.boundaryAfter ?? null,
        notesCount: rail?.notesCount ?? 0,
        reorderStatus: rail?.reorderStatus ?? null,
        printedPageNumber: rail?.printedPageNumber ?? null,
        internalDuplicate: rail?.internalDuplicate ?? false,
        externalDuplicate: rail?.externalDuplicate ?? false,
      };
    });

    const ocrExcerpts: DocumentOcrExcerpt[] = (response.data.ocr_excerpts ?? []).map(excerpt => ({
      id: excerpt.id,
      pageNumber: excerpt.page_number,
      snippet: excerpt.snippet,
      confidence: excerpt.confidence ?? undefined,
      title: excerpt.title ?? undefined,
      tags: Array.isArray(excerpt.tags) ? excerpt.tags : undefined,
    }));

    return {
      header,
      pageRail,
      canvas,
      ocrExcerpts,
      warnings: response.data.warnings ?? [],
      duplicatePairs: response.data.duplicate_pairs ?? [],
      previousDocument: response.data.previous_document ? {
        docId: response.data.previous_document.doc_id,
        title: response.data.previous_document.title,
        pages: response.data.previous_document.pages.map(p => ({
          pageId: p.page_id,
          pageNumber: p.page_number,
          thumbnailUrl: p.thumbnail_url,
          ocrPreview: p.ocr_preview,
          pageIndex: p.page_index,
        })),
        totalPages: response.data.previous_document.total_pages ?? response.data.previous_document.pages.length,
      } : null,
      nextDocument: response.data.next_document ? {
        docId: response.data.next_document.doc_id,
        title: response.data.next_document.title,
        pages: response.data.next_document.pages.map(p => ({
          pageId: p.page_id,
          pageNumber: p.page_number,
          thumbnailUrl: p.thumbnail_url,
          ocrPreview: p.ocr_preview,
          pageIndex: p.page_index,
        })),
        totalPages: response.data.next_document.total_pages ?? response.data.next_document.pages.length,
      } : null,
    };
  },

  async updatePresence(
    metadataPath: string,
    docId: string,
    editorId: string,
  ): Promise<{ activeEditors: string[]; warnings: string[] }> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const encodedDocId = encodeURIComponent(docId);

    const response = await api.post<{
      active_editors?: string[];
      warnings?: string[];
    }>(`/datasets/${encodedMetadata}/documents/${encodedDocId}/presence`, {
      editor_id: editorId,
    });

    return {
      activeEditors: response.data.active_editors ?? [],
      warnings: response.data.warnings ?? [],
    };
  },

  async updateDocument(
    metadataPath: string,
    docId: string,
    payload: UpdateDocumentPayload,
  ): Promise<DocumentOverviewItem> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const encodedDocId = encodeURIComponent(docId);

    const requestBody: Record<string, unknown> = {};
    if (payload.title !== undefined) {
      requestBody.title = payload.title;
    }
    if (payload.confirmTitle !== undefined) {
      requestBody.confirm_title = payload.confirmTitle;
    }
    if (payload.tags !== undefined) {
      requestBody.tags = payload.tags;
    }
    if (payload.confidenceOverride !== undefined) {
      requestBody.confidence_override = payload.confidenceOverride;
    }
    if (payload.notes !== undefined) {
      requestBody.notes = payload.notes;
    }
    if (payload.lastEditedBy !== undefined) {
      requestBody.last_edited_by = payload.lastEditedBy;
    }
    if (payload.date !== undefined) {
      // Serialize to the wire shape the backend reads: snake_case no_date, and
      // drop FE-only fields (precision is derived server-side; confirmed* is set
      // by the backend, not the client). Sending camelCase noDate here would be
      // silently ignored by the backend (which reads no_date).
      if (payload.date === null) {
        requestBody.date = null;
      } else {
        const d = payload.date;
        const wire: Record<string, unknown> = {};
        if (d.raw !== undefined) wire.raw = d.raw;
        if (d.normalized !== undefined) wire.normalized = d.normalized;
        if (d.year !== undefined) wire.year = d.year;
        if (d.month !== undefined) wire.month = d.month;
        if (d.day !== undefined) wire.day = d.day;
        if (d.precision !== undefined) wire.precision = d.precision;
        if (d.noDate !== undefined) wire.no_date = d.noDate;
        requestBody.date = wire;
      }
    }
    if (payload.confirmDate !== undefined) {
      requestBody.confirm_date = payload.confirmDate;
    }
    if (payload.taxonomy !== undefined) {
      requestBody.taxonomy = {
        section: payload.taxonomy.section,
        leaf: payload.taxonomy.leaf ?? null,
        sub_leaf: payload.taxonomy.subLeaf ?? null,
      };
    }
    if (payload.confirmTaxonomy !== undefined) {
      requestBody.confirm_taxonomy = payload.confirmTaxonomy;
    }

    console.debug('[apiService.updateDocument] request', {
      metadataPath,
      docId,
      requestBody,
    });

    const response = await api.patch<RawDocumentOverviewItem>(
      `/datasets/${encodedMetadata}/documents/${encodedDocId}`,
      requestBody,
    );

    console.debug('[apiService.updateDocument] response', {
      docId,
      payload,
      response: response.data,
    });

    return mapDocumentOverviewItem(response.data, encodedMetadata);
  },

  // markDocumentReviewed was removed: review status is DERIVED from confirming
  // the title, date, and category (use updateDocument with confirmTitle /
  // confirmDate / confirmTaxonomy). There is no direct "mark reviewed" call.

  async saveDocument(
    metadataPath: string,
    docId: string,
  ): Promise<{ status: string; savedAt: string; warnings: string[] }> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const encodedDocId = encodeURIComponent(docId);

    const response = await api.post<{
      status: string;
      saved_at: string;
      warnings?: string[];
    }>(`/datasets/${encodedMetadata}/documents/${encodedDocId}/save`);

    return {
      status: response.data.status,
      savedAt: response.data.saved_at,
      warnings: response.data.warnings ?? [],
    };
  },

  async performBulkAction(
    metadataPath: string,
    docId: string,
    options: {
      action: 'move' | 'merge' | 'split' | 'new_document' | 'remove_pages' | 'rotate_pages';
      pageIds: string[];
      targetDocId?: string;
      payload?: Record<string, unknown>;
    },
  ): Promise<{ updatedDocumentIds: string[]; warnings: string[] }> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const encodedDocId = encodeURIComponent(docId);

    const requestBody: Record<string, unknown> = {
      action: options.action,
      page_ids: options.pageIds,
      payload: options.payload ?? {},
    };
    if (options.targetDocId) {
      requestBody.target_doc_id = options.targetDocId;
    }

    const response = await api.post<{
      updated_documents?: string[];
      warnings?: string[];
    }>(`/datasets/${encodedMetadata}/documents/${encodedDocId}/bulk-actions`, requestBody);

    return {
      updatedDocumentIds: response.data.updated_documents ?? [],
      warnings: response.data.warnings ?? [],
    };
  },

  async fetchDocumentMoveTargets(
    metadataPath: string,
    docId: string,
  ): Promise<{ targets: DocumentTargetOption[]; warnings: string[] }> {
    const encodedMetadata = encodeURIComponent(metadataPath);
    const encodedDocId = encodeURIComponent(docId);

    const response = await api.get<{
      targets?: Array<{
        id?: string;
        title?: string;
        docId?: string;
        pageCount?: number;
        status?: string;
        confidence?: number | null;
        previewThumbnail?: string | null;
      }>;
      warnings?: string[];
    }>(`/datasets/${encodedMetadata}/documents/${encodedDocId}/move-targets`);

    const targets: DocumentTargetOption[] = (response.data.targets ?? []).map(target => {
      const docIdValue = target.docId ?? target.id ?? '';
      const status = (target.status as DocumentReviewStatus | undefined) ?? 'unreviewed';
      return {
        id: target.id ?? docIdValue,
        title: target.title ?? docIdValue,
        docId: docIdValue,
        pageCount: target.pageCount ?? 0,
        status,
        confidence: target.confidence ?? null,
        previewThumbnail: target.previewThumbnail ?? null,
      };
    });

    return {
      targets,
      warnings: response.data.warnings ?? [],
    };
  },

  async fetchMetadataFiles(): Promise<string[]> {
    const response = await api.get<string[]>('/metadata_files');
    return response.data;
  },

  async fetchPagesRange(
    metadata: string,
    start: number = 0,
    limit: number = 50,
    threshold?: number
  ): Promise<PagesResponse> {
    const response = await api.get<PagesResponse>('/pages/range', {
      params: { metadata, start, limit, threshold },
    });
    return response.data;
  },

  async fetchDocuments(
    metadata: string,
    threshold?: number
  ): Promise<{ documents: DocumentSummary[]; total_pages: number }> {
    const response = await api.get<{ documents: DocumentSummary[]; total_pages: number }>(
      '/docs',
      {
        params: { metadata, threshold },
      }
    );
    return response.data;
  },

  async fetchFullPage(metadata: string, pageIndex: number): Promise<string> {
    const response = await api.get(`/pages/full/${pageIndex}`, {
      params: { metadata },
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  },

  async fetchPageThumbnail(
    metadata: string,
    pageIndex: number,
    scale: number = 0.25,
  ): Promise<string> {
    const response = await api.get(`/pages/thumb/${pageIndex}`, {
      params: { metadata, scale },
      responseType: 'blob',
    });

    const blob = response.data as Blob;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read thumbnail data'));
        }
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read thumbnail data'));
      reader.readAsDataURL(blob);
    });
  },

  async saveMetadata(
    newPath: string,
    metadata: LinkedMetadata,
    baseMetadata?: string
  ): Promise<{ file_path: string }> {
    const response = await api.post<{ file_path: string }>('/save_metadata', {
      new_path: newPath,
      metadata,
      base_metadata: baseMetadata,
    });
    return response.data;
  },

  // Client-side upload telemetry sidechannel. Fire-and-forget: errors here
  // must never affect the in-flight upload. Uses sendBeacon when available
  // so events queued during page-unload still ship.
  recordUploadEvent(event: UploadTelemetryEvent): void {
    try {
      const body = JSON.stringify(event);
      const url = (process.env.REACT_APP_API_URL || '') + '/upload-events';
      const beacon = (navigator as Navigator & { sendBeacon?: (url: string, data: BodyInit) => boolean }).sendBeacon;
      if (typeof beacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        if (beacon.call(navigator, url, blob)) {
          return;
        }
      }
      // Fallback: fetch with keepalive so it survives navigation.
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Telemetry must never throw.
    }
  },

  // Resumable chunked upload via the tus protocol. Selected from
  // UploadModal; the only live upload path.
  uploadPDFTus(
    file: File,
    opts?: {
      onProgress?: (fraction: number) => void;
      signal?: AbortSignal;
      onTelemetry?: (event: UploadTelemetryEvent) => void;
      uploadId?: string;
      fileFingerprint?: string;
      attemptNum?: number;
      endpoint?: string;
      chunkSize?: number;
      onResume?: () => void;
      // Fires once tusd has assigned a server-side upload ID
      // (immediately after the POST /tus/ create response). The ID is
      // needed so we can register the file with POST /cases.
      onSessionCreated?: (tusUploadId: string) => void;
    },
  ): Promise<{ path: string; size: number; filename: string }> {
    // eslint-disable-next-line no-console
    console.log('[uploadPDFTus entry]', { fileName: file.name, fileSize: file.size, hasSessionCb: !!opts?.onSessionCreated });
    const endpoint = opts?.endpoint || ((process.env.REACT_APP_API_URL || '') + '/tus/');
    const chunkSize = opts?.chunkSize ?? 16 * 1024 * 1024;
    const clientUploadId = opts?.uploadId
      ?? `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const fileFingerprint = opts?.fileFingerprint ?? `${file.name}|${file.size}|${file.lastModified}`;
    const attemptNum = opts?.attemptNum ?? 1;
    const baseEvent = (): Pick<UploadTelemetryEvent, 'uploadId' | 'fileFingerprint' | 'attemptNum' | 'fileSize' | 'fileName' | 'userAgent' | 'connectionType'> => ({
      uploadId: clientUploadId,
      fileFingerprint,
      attemptNum,
      fileSize: file.size,
      fileName: file.name,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      connectionType:
        typeof navigator !== 'undefined' && (navigator as any).connection?.effectiveType
          ? String((navigator as any).connection.effectiveType)
          : undefined,
    });
    const emit = (event: UploadTelemetryEvent): void => {
      try {
        opts?.onTelemetry?.(event);
      } catch {
        // ignore
      }
      apiService.recordUploadEvent(event);
    };

    const startTime = Date.now();
    let lastHeartbeatAt = 0;
    const HEARTBEAT_MS = 15_000;

    return new Promise<{ path: string; size: number; filename: string }>((resolve, reject) => {
      let upload: tus.Upload | null = null;
      let aborted = false;

      const onAbort = () => {
        aborted = true;
        try {
          upload?.abort();
        } catch {
          // ignore
        }
        emit({
          ...baseEvent(),
          eventType: 'abort',
          durationMs: Date.now() - startTime,
        });
        const err: any = new Error('Upload aborted');
        err.name = 'CanceledError';
        reject(err);
      };

      if (opts?.signal) {
        if (opts.signal.aborted) {
          onAbort();
          return;
        }
        opts.signal.addEventListener('abort', onAbort);
      }

      emit({ ...baseEvent(), eventType: 'session_start', byteOffset: 0 });

      upload = new tus.Upload(file, {
        endpoint,
        chunkSize,
        retryDelays: [0, 1000, 3000, 5000, 10000, 30000],
        metadata: {
          filename: file.name,
          filetype: file.type || 'application/pdf',
        },
        // tus-js-client uses localStorage by default; that's fine for
        // browser-side resume. withCredentials is needed so the session
        // cookie reaches our pre-create hook.
        // @ts-expect-error tus-js-client option present at runtime
        withCredentials: true,
        // Default fingerprint is name|size|lastModified, which matches
        // fileFingerprint above — re-selecting the same file in the same
        // browser will surface a previousUploads entry below.
        removeFingerprintOnSuccess: true,
        // tus-js-client option: fires after each HTTP response. Use the
        // POST /tus/ response (which sets Location to the new upload URL)
        // to extract the tus_upload_id.
        //
        // The tus protocol mandates 201 Created for a successful create, but
        // tusd v2.x returns 200 OK in practice. Accept both so we don't get
        // stuck waiting for a session that already exists.
        onAfterResponse(req: any, res: any) {
          try {
            const method = (req.getMethod?.() || '').toUpperCase();
            const status = res.getStatus?.();
            const createdOk = status === 201 || status === 200;
            // TEMP DEBUG: surface what we see on every response so we can
            // confirm onSessionCreated firing conditions in DevTools.
            // Remove once case-create wiring is verified.
            // eslint-disable-next-line no-console
            console.log('[tus onAfterResponse]', {
              method,
              status,
              hasCb: !!opts?.onSessionCreated,
              createdOk,
              location: res.getHeader?.('Location'),
              uploadUrl: upload?.url,
            });
            if (method === 'POST' && createdOk && opts?.onSessionCreated) {
              const location = res.getHeader?.('Location') || upload?.url;
              if (typeof location === 'string') {
                const tusUploadId = location.split('/').filter(Boolean).pop() || '';
                // eslint-disable-next-line no-console
                console.log('[tus onSessionCreated firing]', { location, tusUploadId });
                if (tusUploadId) opts.onSessionCreated(tusUploadId);
              }
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[tus onAfterResponse error]', err);
          }
        },
        onError(error: Error) {
          if (aborted) return;
          emit({
            ...baseEvent(),
            eventType: 'chunk_failure',
            errorClass: error?.name || 'TusError',
            durationMs: Date.now() - startTime,
          });
          reject(error);
        },
        onProgress(bytesUploaded: number, bytesTotal: number) {
          if (bytesTotal) {
            opts?.onProgress?.(Math.max(0, Math.min(1, bytesUploaded / bytesTotal)));
          }
          const now = Date.now();
          if (now - lastHeartbeatAt >= HEARTBEAT_MS) {
            lastHeartbeatAt = now;
            emit({
              ...baseEvent(),
              eventType: 'session_heartbeat',
              byteOffset: bytesUploaded,
              durationMs: now - startTime,
            });
          }
        },
        onChunkComplete(_chunkSize: number, bytesAccepted: number) {
          emit({
            ...baseEvent(),
            eventType: 'chunk_success',
            byteOffset: bytesAccepted,
            durationMs: Date.now() - startTime,
          });
        },
        onShouldRetry(_err: Error, retryAttempt: number) {
          emit({
            ...baseEvent(),
            eventType: 'retry',
            attemptNum: attemptNum + retryAttempt + 1,
            durationMs: Date.now() - startTime,
          });
          return true;
        },
        async onSuccess() {
          // tus URL ends with /<tus-upload-id>. Parse that out and ask the
          // backend for the file's resolved relative path under the dataset
          // root (set up by the post-finish hook).
          const url = upload?.url || '';
          const tusUploadId = url.split('/').filter(Boolean).pop() || '';
          try {
            const lookup = await api.get<{ path: string }>(
              `/uploads/by-tus-id/${encodeURIComponent(tusUploadId)}`,
            );
            emit({
              ...baseEvent(),
              eventType: 'session_complete',
              byteOffset: file.size,
              httpStatus: 200,
              durationMs: Date.now() - startTime,
            });
            resolve({ path: lookup.data.path, size: file.size, filename: file.name });
          } catch (err: any) {
            emit({
              ...baseEvent(),
              eventType: 'chunk_failure',
              httpStatus: err?.response?.status ?? 0,
              errorClass: 'ResolutionLookupFailed',
              durationMs: Date.now() - startTime,
            });
            reject(err);
          }
        },
      });

      (async () => {
        try {
          const previousUploads = await upload!.findPreviousUploads();
          if (previousUploads.length > 0 && !aborted) {
            const prior = previousUploads[0];
            upload!.resumeFromPreviousUpload(prior);
            opts?.onResume?.();
            emit({
              ...baseEvent(),
              eventType: 'resume',
              durationMs: Date.now() - startTime,
            });
            // When resuming, tus-js-client skips the POST create entirely,
            // so onAfterResponse never sees a 2xx POST and onSessionCreated
            // would never fire. Extract the tus_upload_id from the resumed
            // URL ourselves and notify the caller — the case registration
            // in UploadsContext needs this to proceed.
            const priorUrl = (prior as any)?.uploadUrl || upload?.url;
            if (typeof priorUrl === 'string' && opts?.onSessionCreated) {
              const tusUploadId = priorUrl.split('/').filter(Boolean).pop() || '';
              if (tusUploadId) {
                // eslint-disable-next-line no-console
                console.log('[tus onSessionCreated firing from resume]', { priorUrl, tusUploadId });
                opts.onSessionCreated(tusUploadId);
              }
            }
          }
        } catch {
          // localStorage unavailable or fingerprint lookup failed; fall
          // through to a fresh upload.
        }
        if (!aborted) {
          upload!.start();
        }
      })();
    });
  },

  // Create a case-with-pending-uploads job. The backend records the
  // case in status UPLOADING immediately; tusd's post-finish hook
  // transitions it to PENDING (worker pickup) once all files have
  // finished uploading.
  async createCase(
    caseName: string | undefined,
    files: { filename: string; expected_size: number; tus_upload_id: string }[],
    ocr?: Record<string, unknown>,
  ): Promise<{ job_id: string; case_id: string; status: string; status_endpoint: string }> {
    const body: Record<string, unknown> = { files };
    if (caseName && caseName.trim()) {
      body.case_name = caseName.trim();
    }
    if (ocr) {
      body.ocr = ocr;
    }
    const response = await api.post<{
      job_id: string;
      case_id: string;
      status: string;
      status_endpoint: string;
    }>('/cases', body);
    return response.data;
  },

  async listUploads(): Promise<{
    uploads: Array<{
      filename: string;
      size: number;
      uploaded_at: number;
      path: string;
    }>;
  }> {
    const response = await api.get<{
      uploads: Array<{
        filename: string;
        size: number;
        uploaded_at: number;
        path: string;
      }>;
    }>('/uploads');
    return response.data;
  },

  async runInference(metadataPath: string): Promise<{
    message: string;
    job_id: string;
    status_endpoint: string;
    case_id: string | null;
  }> {
    const response = await api.post<{
      message: string;
      job_id: string;
      status_endpoint: string;
      case_id: string | null;
    }>('/run_inference', null, {
      params: { metadata_path: metadataPath },
    });
    return response.data;
  },

  async reprocessDataset(metadataPath: string, operations: string[]): Promise<{
    message: string;
    job_id: string;
    status_endpoint: string;
    case_id: string | null;
  }> {
    const encoded = encodeURIComponent(metadataPath);
    const response = await api.post<{
      message: string;
      job_id: string;
      status_endpoint: string;
      case_id: string | null;
    }>(`/datasets/${encoded}/reprocess`, { operations });
    return response.data;
  },

  async pullFromFilevine(
    metadataPath: string,
    submission: {
      /** Every referenced document with the picker's metadata (real filename +
       *  uploadDate) so the import records true provenance for all pulls. */
      documents: Array<{ documentId: number; filename?: string | null; uploadDate?: string | null }>;
      /** Ordered group to import as ONE combined document (fragments). */
      group?: { documentIds: number[]; title?: string | null } | null;
    },
  ): Promise<{
    case_id: string;
    jobs: Array<{
      document_id: number;
      document_ids?: number[] | null;
      job_id: string;
      status: string;
      detail?: string | null;
      status_endpoint: string;
    }>;
  }> {
    const encoded = encodeURIComponent(metadataPath);
    const groupIds = new Set(submission.group?.documentIds ?? []);
    const singleIds = submission.documents
      .map(d => d.documentId)
      .filter(id => !groupIds.has(id));
    const response = await api.post<{
      case_id: string;
      jobs: Array<{
        document_id: number;
        document_ids?: number[] | null;
        job_id: string;
        status: string;
        detail?: string | null;
        status_endpoint: string;
      }>;
    }>(`/datasets/${encoded}/filevine/pull`, {
      document_ids: singleIds.length > 0 ? singleIds : null,
      group: submission.group
        ? { document_ids: submission.group.documentIds, title: submission.group.title ?? null }
        : null,
      documents: submission.documents.map(d => ({
        document_id: d.documentId,
        filename: d.filename ?? null,
        upload_date: d.uploadDate ?? null,
      })),
    });
    return response.data;
  },

  async pushToFilevine(
    metadataPath: string,
    opts: { includeSourceFooter?: boolean; documentIds?: string[] } = {},
  ): Promise<{
    job_id: string;
    case_id: string | null;
  }> {
    const includeSourceFooter = opts.includeSourceFooter ?? true;
    // metadata + include_source_footer are query params; document_ids (optional,
    // scopes the push to selected docs) rides in the JSON body since it's a list.
    const body = opts.documentIds && opts.documentIds.length > 0
      ? { document_ids: opts.documentIds }
      : {};
    const response = await api.post<{ job_id: string; case_id: string | null }>(
      `/push_to_filevine?metadata=${encodeURIComponent(metadataPath)}` +
        `&include_source_footer=${includeSourceFooter}`,
      body,
    );
    return response.data;
  },

  async searchFilevineProjects(query: string): Promise<{
    projects: Array<{
      project_id: number;
      project_name: string;
      number: string | null;
      phase_name: string | null;
      is_archived: boolean;
      already_linked_case_id: string | null;
    }>;
  }> {
    const response = await api.get<{
      projects: Array<{
        project_id: number;
        project_name: string;
        number: string | null;
        phase_name: string | null;
        is_archived: boolean;
        already_linked_case_id: string | null;
      }>;
    }>(`/filevine/project_search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  async listFilevineDocuments(
    metadataPath: string,
    params: { q?: string; offset?: number } = {},
  ): Promise<{
    documents: Array<{
      document_id: number;
      filename: string;
      folder_name: string | null;
      size: number | null;
      upload_date: string | null;
      already_pulled: boolean;
    }>;
    offset: number;
    has_more: boolean;
    count: number | null;
  }> {
    const encoded = encodeURIComponent(metadataPath);
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.offset) search.set('offset', String(params.offset));
    const qs = search.toString();
    const response = await api.get<{
      documents: Array<{
        document_id: number;
        filename: string;
        folder_name: string | null;
        size: number | null;
        upload_date: string | null;
        already_pulled: boolean;
      }>;
      offset: number;
      has_more: boolean;
      count: number | null;
    }>(`/datasets/${encoded}/filevine/documents${qs ? `?${qs}` : ''}`, {
      timeout: FILEVINE_PICKER_TIMEOUT_MS,
    });
    return response.data;
  },

  async listFilevineFolders(
    metadataPath: string,
    parentFolderId?: number,
  ): Promise<{
    parent_folder_id: number | null;
    folders: Array<{
      folder_id: number;
      name: string;
      has_children: boolean | null;
    }>;
    documents: Array<{
      document_id: number;
      filename: string;
      folder_name: string | null;
      size: number | null;
      upload_date: string | null;
      already_pulled: boolean;
    }>;
    documents_truncated: boolean;
  }> {
    const encoded = encodeURIComponent(metadataPath);
    const qs =
      parentFolderId !== undefined
        ? `?parent_folder_id=${parentFolderId}`
        : '';
    const response = await api.get<{
      parent_folder_id: number | null;
      folders: Array<{
        folder_id: number;
        name: string;
        has_children: boolean | null;
      }>;
      documents: Array<{
        document_id: number;
        filename: string;
        folder_name: string | null;
        size: number | null;
        upload_date: string | null;
        already_pulled: boolean;
      }>;
      documents_truncated: boolean;
    }>(`/datasets/${encoded}/filevine/folders${qs}`, {
      timeout: FILEVINE_PICKER_TIMEOUT_MS,
    });
    return response.data;
  },

  async resolveFilevineProject(params: { projectId?: number; name?: string }): Promise<{
    project_id: number;
    project_name: string;
    number: string | null;
    already_linked_case_id: string | null;
  }> {
    const response = await api.post<{
      project_id: number;
      project_name: string;
      number: string | null;
      already_linked_case_id: string | null;
    }>('/filevine/resolve_project', {
      project_id: params.projectId ?? null,
      name: params.name ?? null,
    });
    return response.data;
  },

  async linkFilevineProject(params: {
    projectId: number;
    projectName: string;
    caseName?: string;
    useProjectName: boolean;
  }): Promise<{ case_id: string; metadata_path: string; project_id: number; project_name: string }> {
    const response = await api.post<{
      case_id: string;
      metadata_path: string;
      project_id: number;
      project_name: string;
    }>('/filevine/link_project', {
      project_id: params.projectId,
      project_name: params.projectName,
      case_name: params.caseName ?? null,
      use_project_name: params.useProjectName,
    });
    return response.data;
  },

  async reprocessDocument(metadataPath: string, documentId: string, operations: string[]): Promise<{
    message: string;
    job_id: string;
    status_endpoint: string;
    case_id: string | null;
  }> {
    const response = await api.post<{
      message: string;
      job_id: string;
      status_endpoint: string;
      case_id: string | null;
    }>('/document_reprocess', {
      metadata_path: metadataPath,
      document_id: documentId,
      operations,
    });
    return response.data;
  },

  async cancelJob(jobId: string): Promise<ActiveJob> {
    const response = await api.post(`/cancel_job/${encodeURIComponent(jobId)}`);
    return mapActiveJob({ job_id: jobId, ...response.data });
  },

  async fetchTrackedJobs(params: {
    status?: string[];
    metadataPath?: string;
    datasetPath?: string;
    jobType?: string;
    excludeJobTypes?: string[];
    limit?: number;
    order?: 'asc' | 'desc' | 'queue';
  } = {}): Promise<{ jobs: TrackedJob[]; total: number }> {
    const response = await api.get<{ jobs: TrackedJob[]; total: number }>('/jobs', {
      params: {
        status: params.status,
        metadata_path: params.metadataPath,
        dataset_path: params.datasetPath,
        job_type: params.jobType,
        exclude_job_types: params.excludeJobTypes,
        limit: params.limit,
        order: params.order,
      },
    });
    return response.data;
  },

  async fetchTrackedJob(
    jobId: string,
    options: { includeEvents?: boolean; eventsLimit?: number; eventsOrder?: 'asc' | 'desc' } = {},
  ): Promise<{ job: TrackedJob; events: TrackedJobEvent[] }> {
    const response = await api.get<{ job: TrackedJob; events: TrackedJobEvent[] }>(`/jobs/${encodeURIComponent(jobId)}`, {
      params: {
        include_events: options.includeEvents ?? false,
        events_limit: options.eventsLimit,
        events_order: options.eventsOrder,
      },
    });
    return response.data;
  },

  async fetchTrackedJobEvents(
    jobId: string,
    options: { limit?: number; order?: 'asc' | 'desc' } = {},
  ): Promise<{ jobId: string; events: TrackedJobEvent[] }> {
    const response = await api.get<{ jobId: string; events: TrackedJobEvent[] }>(
      `/jobs/${encodeURIComponent(jobId)}/events`,
      {
        params: {
          limit: options.limit,
          order: options.order,
        },
      },
    );
    return response.data;
  },

  async deleteJobRecord(jobId: string): Promise<{ jobId: string; removed: boolean }> {
    const response = await api.delete(`/jobs/${encodeURIComponent(jobId)}`);
    const data = response.data ?? {};
    return {
      jobId: String(data.job_id ?? jobId),
      removed: Boolean(data.removed),
    };
  },

  async exportPDF(
    metadata: string,
    annotatedBoundaries: Array<number | null>,
    probs: (number | null)[],
    threshold: number = 0.5
  ): Promise<{
    message: string;
    export_dir: string;
    documents: Array<{
      doc_id: string;
      success: boolean;
      page_count: number;
      page_range: [number, number];
      file_path?: string;
      file_size?: number;
      error?: string;
    }>;
    archive_path: string | null;
    archive_size: number;
    total_documents: number;
    successful_exports: number;
  }> {
    const response = await api.post<{
      message: string;
      export_dir: string;
      documents: Array<{
        doc_id: string;
        success: boolean;
        page_count: number;
        page_range: [number, number];
        file_path?: string;
        file_size?: number;
        error?: string;
      }>;
      archive_path: string | null;
      archive_size: number;
      total_documents: number;
      successful_exports: number;
    }>('/export_pdf', {
      metadata,
      annotated_boundaries: annotatedBoundaries,
      probs,
      threshold,
    });
    return response.data;
  },

  async saveGaps(
    metadata: string,
    annotations: Array<number | null>,
    probs?: Array<number | null>
  ): Promise<{ message: string }>{
    const response = await api.post<{ message: string }>(
      '/save_gaps',
      {
        metadata,
        annotations,
        probs,
      }
    );
    return response.data;
  },

  async exportDocumentPDF(
    metadataPath: string,
    docId: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await api.post<Blob>(
      '/export_document_pdf',
      null,
      {
        params: {
          metadata: metadataPath,
          doc_id: docId,
        },
        responseType: 'blob',
      }
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${docId}.pdf`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    return { blob: response.data, filename };
  },

  async exportCasePDF(
    metadataPath: string,
  ): Promise<{ job_id: string; case_id: string }> {
    const response = await api.post<{ job_id: string; case_id: string }>(
      '/export_case_pdf',
      null,
      {
        params: {
          metadata: metadataPath,
        },
      }
    );
    return response.data;
  },

  async downloadExport(archivePath: string): Promise<Blob> {
    const response = await api.get<Blob>(
      `/download/${archivePath}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * @deprecated Backed the now-deprecated DuplicatesModal pair-by-pair view
   * (see DuplicatesModal.tsx for full context). Retained because the binary
   * external-duplicate badge derives from the same backend data, and a
   * future detail surface may reuse this fetch. Not called by the live UI.
   */
  async fetchDuplicates(metadataPath: string, documentId?: string): Promise<DuplicatePagesResponse> {
    const apiUrl = `/datasets/${encodeURIComponent(metadataPath)}/duplicates`;
    console.log('[API] Fetching duplicates from:', apiUrl);
    try {
      const response = await api.get<DuplicatePagesResponse>(apiUrl);
      console.log('[API] Duplicates response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Failed to fetch duplicates:', error);
      throw error;
    }
  },

  getDownloadUrl(filePath: string): string {
    // Construct relative URL so it works both when proxied in dev and when
    // served by the backend in production.
    return `/download/${encodeURIComponent(filePath)}`;
  },

  getExportJobIdForCase(metadataPath: string): string {
    // Must match backend's _export_job_id logic which uses sha256 hash
    // This is a simplified hash to approximate the backend's deterministic ID
    const canonical = metadataPath.replace(/\\/g, '/');
    let hash = 0;
    for (let i = 0; i < canonical.length; i++) {
      hash = ((hash << 5) - hash) + canonical.charCodeAt(i);
      hash = hash & hash;
    }
    return `export_case_${Math.abs(hash).toString(16).padStart(16, '0').slice(0, 16)}`;
  },
};

export default apiService;
